import { painStats } from './context';
import { dayAggregate } from './dailyLogs';
import { DAY_MS, MONTH_LONG, fm, fmy, fr, longDate, toIso, toMs } from './dates';
import { bin, num, pl, r1 } from './format';
import { ORNG, ORNG_TEXT, PHASE_STYLE, RAMP_FG, RUST, RUST_TEXT } from './palette';

// The calendar (Wellness Calendar Simple's `cal`) and the day detail panel
// (Wellness Day Detail's `detail`), ported from the design's cal()/detail()/
// val() onto the raw logs. Every scale is 0–5.

const FLOW_BIN = { light: 1, medium: 2, heavy: 4 };
const isOrange = (metric) => metric === 'energy' || metric === 'cravings';

/**
 * One day's recorded value for a metric.
 * @returns {{ st: 'val'|'zero'|'miss'|'na', val?: string, idx?: number, txt: string }}
 */
export function dayValue(ctx, metric, iso, info) {
  if (metric === 'periods') {
    const log = ctx.logs.period[iso];
    if (log?.flow) {
      return { st: 'val', val: log.flow[0], idx: FLOW_BIN[log.flow.toLowerCase()] ?? 2, txt: `${log.flow} flow` };
    }
    if (log?.spotting) return { st: 'zero', val: 'S', txt: 'Spotting' };
    // README gap G13: a period day logged as "Not specified", or not logged.
    if (info?.per || log?.flowNotRecorded) return { st: 'miss', txt: 'Period day · flow not recorded' };
    return { st: 'na', txt: 'No period or spotting recorded' };
  }
  if (metric === 'energy') {
    const v = ctx.logs.energy[iso];
    if (v == null) return { st: 'miss', txt: 'No entry recorded' };
    if (v === 0) return { st: 'zero', val: '0', txt: '0/5 · Drained' };
    return { st: 'val', val: num(v), idx: Math.max(0, Math.min(4, Math.ceil(v) - 1)), txt: `${num(v)}/5` };
  }
  const d = dayAggregate(ctx.logs[metric][iso]);
  if (!d) return { st: 'miss', txt: 'No entry recorded' };
  if (d.none) return { st: 'zero', val: '0', txt: metric === 'pms' ? '0/5 — client confirmed no symptoms' : 'No cravings today (recorded)' };
  return {
    st: 'val',
    val: r1(d.avg),
    idx: bin(d.avg, 5),
    txt: `${r1(d.avg)}/5 daily average · ${pl(d.n, metric === 'pms' ? 'symptom' : 'category')} rated`,
  };
}

const ZERO_CHIP = { bg: '#fff', ring: 'inset 0 0 0 1.5px #CFC4BB' };
const MISS_CHIP = { bg: '#fff', ring: 'inset 0 0 0 1px #E3DCD5', txt: '—' };

const LEGENDS = {
  periods: ['Flow', ['Light', 'Medium', 'Heavy'].map((l, j) => ({ bg: RUST[[1, 2, 4][j]], label: `${l[0]} · ${l}` })),
    [{ ...ZERO_CHIP, txt: 'S', label: 'Spotting' }, { ...MISS_CHIP, label: 'Period day, flow not recorded' }], 'Flow and spotting per day'],
  pms: ['Daily average severity · 0–5', ['0.1–1', '1.1–2', '2.1–3', '3.1–4', '4.1–5'].map((l, j) => ({ bg: RUST[j], label: l })),
    [{ ...ZERO_CHIP, txt: '0', label: 'Recorded 0 · no symptoms' }, { ...MISS_CHIP, label: 'No entry recorded' }], 'Daily average of rated symptoms'],
  energy: ['Energy rating · 0–5', ['1', '2', '3', '4', '5'].map((l, j) => ({ bg: ORNG[j], label: l })),
    [{ ...ZERO_CHIP, txt: '0', label: 'Recorded 0 · drained' }, { ...MISS_CHIP, label: 'No entry recorded' }], 'One rating per day'],
  cravings: ['Daily average intensity · 0–5', ['0.1–1', '1.1–2', '2.1–3', '3.1–4', '4.1–5'].map((l, j) => ({ bg: ORNG[j], label: l })),
    [{ ...ZERO_CHIP, txt: '0', label: 'No cravings today (recorded)' }, { ...MISS_CHIP, label: 'No entry recorded' }], 'Daily average of rated categories'],
};

/**
 * Month grid, Monday first.
 * @param {{year: number, month: number}} mon - month 0-based.
 * @param {{ canPrev: boolean, canNext: boolean }} nav
 */
export function calendar(ctx, metric, mon, selected, nav) {
  const ramp = isOrange(metric) ? ORNG : RUST;
  const first = Date.UTC(mon.year, mon.month, 1);
  const last = Date.UTC(mon.year, mon.month + 1, 0);
  const offset = (new Date(first).getUTCDay() + 6) % 7;
  const phaseOf = (iso) => ctx.info(iso)?.phaseKey ?? null;
  const cells = Array.from({ length: offset }, () => ({ blank: true }));

  for (let t = first, i = 0; t <= last; t += DAY_MS, i++) {
    const iso = toIso(t);
    const future = iso > ctx.today;
    const info = ctx.info(iso);
    const v = future ? { st: 'fut' } : dayValue(ctx, metric, iso, info);
    const dow = (offset + i) % 7;
    const isSel = iso === selected;
    const S = {
      val: [ramp[v.idx], RAMP_FG[v.idx], 'none'],
      zero: ['#fff', '#1f1f1f', 'inset 0 0 0 1.5px #CFC4BB'],
      miss: ['#fff', '#8a8a8a', 'inset 0 0 0 1px #E3DCD5'],
      na: ['#F7F4F1', '#A39A93', 'none'],
      fut: ['#fff', '#C8C8C8', 'inset 0 0 0 1px #F4F0EC'],
    }[v.st];
    const P = phaseOf(iso);
    const prevP = i > 0 && dow > 0 ? phaseOf(toIso(t - DAY_MS)) : null;
    const nextP = t < last && dow < 6 ? phaseOf(toIso(t + DAY_MS)) : null;
    const Q = P ? PHASE_STYLE[P] : null;
    cells.push({
      blank: false,
      iso,
      day: new Date(t).getUTCDate(),
      val: v.st === 'miss' ? '—' : v.val || '',
      bg: S[0],
      fg: S[1],
      dop: v.st === 'na' ? '.7' : '1',
      ring: isSel ? '0 0 0 2px #fff, 0 0 0 4px #1f1f1f' : S[2],
      clickable: !future,
      tip: future ? '' : `${fmy(iso)}${info ? ` · Cycle day ${info.cd}` : ''} · ${v.txt}${Q ? ` · ${Q.label} phase` : ''}`,
      phBg: Q ? Q.bg : 'transparent',
      phW: P && nextP === P ? 'calc(100% + 6px)' : '100%',
      phLabel: P && prevP !== P ? Q.label : '',
      phFg: Q ? Q.fg : '#555',
    });
  }
  const [rampTitle, bins, legend, sub] = LEGENDS[metric];
  return {
    title: 'Daily calendar',
    sub: `${sub} · selected month only`,
    month: `${MONTH_LONG[mon.month]} ${mon.year}`,
    cells,
    rampTitle: rampTitle.toUpperCase(),
    bins,
    legend,
    canPrev: nav.canPrev,
    canNext: nav.canNext,
  };
}

/** The Day Detail panel ("DAILY LOG") for one day. */
export function dayDetail(ctx, metric, iso) {
  const f = ctx.info(iso);
  const v = dayValue(ctx, metric, iso, f);
  const ctxLine = !f ? 'Before the first logged period'
    : f.per ? `Period day ${f.cd} of ${f.cycle.periodDays}`
      : f.phaseKey !== 'est' && f.phase ? `${f.phase} phase · estimated by Period card`
        : 'Phase unavailable · cycle still ongoing';
  const d = {
    date: longDate(iso),
    sub: f ? `Day ${f.cd} of cycle ${f.cycle.n}${f.cycle.completed ? '' : ' (ongoing)'}` : 'No cycle recorded yet',
    ctx: ctxLine,
    ctxBg: f?.per ? '#B5654A' : 'transparent',
    ctxRing: f?.per ? 'none' : 'inset 0 0 0 1px #CFC4BB',
    state: v.txt,
    rows: [],
    facts: [],
    hasRows: false,
    hasFacts: false,
    foot: '',
  };
  const mx = metric === 'periods' ? 0 : 5;
  d.valueLabel = { periods: 'FLOW', pms: 'DAILY SCORE', energy: 'ENERGY', cravings: 'DAILY INTENSITY' }[metric];
  const log = ctx.logs.period[iso];
  d.value = v.st === 'miss' || v.st === 'na' ? '—' : metric === 'periods' ? log?.flow || (log?.spotting ? 'Spotting' : '—') : v.val;
  d.unit = (v.st === 'val' || v.st === 'zero') && mx ? `/${mx}` : '';
  d.valueFg = v.st === 'val' ? (isOrange(metric) ? ORNG_TEXT : RUST_TEXT)[v.idx] : '#1f1f1f';

  if (metric === 'pms' || metric === 'cravings') {
    const a = dayAggregate(ctx.logs[metric][iso]);
    const ramp = metric === 'pms' ? RUST : ORNG;
    if (a && a.n) {
      const active = a.list.filter((s) => s.val > 0).length;
      d.hasRows = true;
      d.rowsTitle = metric === 'pms' ? `TRACKED SYMPTOMS · ${a.n} RATED` : `CRAVING CATEGORIES · ${active} ACTIVE`;
      d.rows = a.list.slice().sort((p, q) => q.val - p.val).map((s) => ({
        name: s.name,
        txt: num(s.val),
        w: `${Math.max((s.val / mx) * 100, 0)}%`,
        bg: ramp[bin(Math.max(s.val, 0.1), mx)],
        ring: s.val === 0 ? 'inset 0 0 0 1px #CFC4BB' : 'none',
      }));
      d.foot = `Average = ${num(a.sum)} ÷ ${a.n} rated`;
    } else {
      d.foot = a ? 'Explicit zero entry — counts as logged' : 'Missing entries are never shown as 0';
    }
  }
  if (metric === 'energy') {
    const e = ctx.logs.energy[iso];
    d.hasFacts = true;
    d.facts = [
      { k: 'Rating', v: e == null ? '—' : e === 0 ? '0/5 · Drained' : `${num(e)}/5` },
      { k: 'Phase', v: f && f.phaseKey !== 'est' && f.phase ? `${f.phase} (estimated)` : 'Unavailable — cycle ongoing' },
    ];
    d.foot = 'Scale 0–5 · 0 = Drained';
  }
  if (metric === 'periods' && f) {
    const c = f.cycle;
    const pain = log?.pain;
    const s = f.per ? painStats(ctx, c.start, c.periodEnd) : null;
    d.hasFacts = true;
    d.facts = [
      { k: 'Period', v: f.per ? fr(c.start, c.periodEnd) : log?.spotting ? 'Spotting only' : 'Not a period day' },
      { k: 'Spotting', v: log?.spotting ? 'Recorded' : 'None recorded' },
      { k: 'Pain', v: !f.per ? 'Not a period day' : pain == null ? 'No entry recorded' : pain === 0 ? '0/5 · pain-free' : `${num(pain)}/5` },
      ...(s ? [{ k: 'This period · highest', v: s.hi ? `${num(s.hi.v)}/5 · ${fm(s.hi.date)}` : '—' }] : []),
      { k: 'Cycle length', v: c.length ? `${c.length} days` : `${pl(Math.round((toMs(ctx.today) - toMs(c.start)) / DAY_MS) + 1, 'day')} so far · ongoing` },
    ];
    d.foot = 'Pain scale 0–5';
  }
  return d;
}
